<template>
  <div class="pdf-document-editor">
    <div class="editor-header" :class="{ 'preview-mode-header': isPreviewMode }">
      <div class="header-left">
        <h2>{{ editorHeader }}</h2>
        <span v-if="templateName" class="template-name">{{ templateName }}</span>
      </div>
      <div class="header-actions">
        <!-- Preview Mode Actions -->
        <template v-if="isPreviewMode">
          <div class="preview-mode-badge">
            <i class="fa fa-eye"></i>
            <span>Preview Mode</span>
            <small>Viewing as student</small>
          </div>
          <button class="btn btn-primary" @click="$emit('edit-template')">
            <i class="fa fa-pencil"></i> Edit Template
          </button>
          <button class="btn btn-default" @click="$emit('cancel')">
            <i class="fa fa-times"></i> Close
          </button>
        </template>

        <!-- Existing Create/Edit Mode Actions -->
        <template v-else-if="showEditorSidebar">
          <button class="btn btn-success" @click="saveTemplate"
            :disabled="saving || !pdfLoaded || annotations.length === 0">
            <i class="fa fa-save" v-if="!saving"></i>
            <i class="fa fa-spinner fa-spin" v-else></i>
            {{ saving ? 'Saving...' : isTemplateEditMode ? 'Update Template' : 'Save Template' }}
          </button>
          <button class="btn btn-default" @click="$emit('cancel')">
            Cancel
          </button>
        </template>

        <!-- Document Completion Mode Actions -->
        <template v-else>
          <button class="btn btn-primary" @click="submitDocument"
            :disabled="submitting || !(allRequiredFieldsCompleted || false)">
            <i class="fa fa-check" v-if="!submitting"></i>
            <i class="fa fa-spinner fa-spin" v-else></i>
            {{ submitting ? 'Submitting...' : 'Submit Document' }}
          </button>
          <button class="btn btn-default" @click="$emit('cancel')">
            Cancel
          </button>
        </template>
      </div>
    </div>

    <div class="editor-body">
      <div class="editor-main">
        <div v-if="!pdfLoaded && !pdfUrl" class="pdf-upload-area">
          <div class="upload-content">
            <i class="fa fa-file-pdf-o fa-3x"></i>
            <h3>Upload PDF Document</h3>
            <p>Select a PDF file to create a template</p>
            <input data-testid="pdf-file-input" type="file" accept=".pdf" @change="handleFileUpload" ref="fileInput" class="file-input" />
            <button class="btn btn-primary" @click="$refs.fileInput.click()">
              Choose PDF File
            </button>
          </div>
        </div>

        <PDFViewer v-else-if="pdfLoaded || pdfUrl" :pdf-url="pdfUrl" :pdf-file="pdfFile" @page-change="onPageChange"
          @pdf-loaded="onPdfLoaded" @scale-change="onScaleChange" @error="onPdfError" ref="pdfViewer">
          <template #annotations>
            <component v-for="annotation in currentPageAnnotations" :key="annotation.id"
              :is="getFieldComponent(annotation.type)" :annotation="annotation"
              :value="getFieldValue(annotation.fieldName)" :scale="pdfScale"
              :selected="selectedAnnotation && selectedAnnotation.id === annotation.id"
              :editing="editingField === annotation.fieldName" :editable="showEditorSidebar" :show-label="false"
              @field-click="handleSelectAnnotation" @position-change="handleUpdateAnnotationPosition"
              @value-change="updateFieldValue" @open-signature="openSignatureModal"
              @edit-end="handleStopEditingField" />
          </template>

          <template #overlay>
            <!-- Drop zone for drag-and-drop field placement -->
            <div v-if="showEditorSidebar" class="drop-zone"
              :class="{ 'drag-over': isDraggingOver, 'drag-active': isDraggingField }" @drop="handleDrop"
              @dragover.prevent="handleDragOver" @dragenter.prevent="handleDragEnter" @dragleave="handleDragLeave">
            </div>

            <!-- Click overlay for adding new fields (fallback) -->
            <div v-if="addingField" class="click-overlay" @click="handleCanvasClick"></div>
          </template>
        </PDFViewer>

        <!-- Ghost Preview Component -->
        <field-ghost-preview :is-dragging="isDraggingField" :drag-type="dragFieldType"
          :cursor-position="ghostCursorPosition" :is-valid-drop-zone="isDraggingOver" />
      </div>

      <div class="editor-sidebar" v-if="showEditorSidebar">
        <!-- Template Metadata Form -->
        <div v-if="mode === 'create'" class="template-metadata-section">
          <h5 class="section-title">Template Details</h5>
          <div class="form-group">
            <label for="template-name" class="form-label">Template Name *</label>
            <input type="text" data-testid="template-name" id="template-name" class="form-control" v-model="localTemplateName"
              @input="$emit('metadata-change', { field: 'name', value: localTemplateName })"
              placeholder="Enter template name" required />
          </div>
          <div class="form-group">
            <label for="template-description" class="form-label">Description</label>
            <textarea data-testid="template-description" id="template-description" class="form-control" v-model="localTemplateDescription"
              @input="$emit('metadata-change', { field: 'description', value: localTemplateDescription })"
              placeholder="Enter template description" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label for="template-status" class="form-label">Status</label>
            <select data-testid="template-status" id="template-status" class="form-control" v-model="localTemplateStatus"
              @change="$emit('metadata-change', { field: 'status', value: localTemplateStatus })">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
          </div>
        </div>

        <annotation-toolbar :selected-field="selectedAnnotation" @start-add-field="handleStartAddingField"
          @cancel-add-field="handleCancelAddingField" @drag-start="handleToolbarDragStart"
          @drag-end="handleToolbarDragEnd" ref="toolbar" />

        <!-- Property Panel replaced by ContextualFieldToolbar -->
        <!-- <property-panel v-if="selectedAnnotation" :annotation="selectedAnnotation" :allAnnotations="annotations"
          @update="handleUpdateAnnotation" @delete="handleDeleteAnnotation" @validate-field-name="validateFieldName" /> -->

        <!-- Signature requirements removed - signatures are now just form fields with type='signature' -->

        <div v-if="annotations.length > 0" class="annotations-summary">
          <h5>Fields ({{ annotations.length }})</h5>
          <div class="annotations-list">
            <div v-for="annotation in annotations" :key="annotation.id" class="annotation-item"
              :class="{ 'selected': selectedAnnotation && selectedAnnotation.id === annotation.id }"
              @click="handleSelectAnnotation(annotation)">
              <i :class="getFieldIcon(annotation.type)"></i>
              <span>{{ annotation.label || annotation.fieldName }}</span>
              <span class="annotation-type">{{ annotation.type }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- User completion interface (also used for preview) -->
      <div class="editor-sidebar preview-sidebar" v-if="isPreviewMode || (mode === 'edit' && userAssignedFields.length > 0)">
        <div class="preview-info" v-if="isPreviewMode">
          <div class="preview-info-icon">
            <i class="fa fa-graduation-cap"></i>
          </div>
          <h5>Student View</h5>
          <p>This is how students will see and complete this document.</p>
        </div>

        <div class="completion-progress">
          <h5>{{ isPreviewMode ? 'Form Fields' : 'Your Fields' }}</h5>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: completionPercentage + '%' }"></div>
          </div>
          <p>{{ completedFields }} of {{ userAssignedFields.length }} completed</p>
        </div>

        <div class="field-list">
          <div v-for="field in userAssignedFields" :key="field.fieldName" class="field-item" :class="{
            'completed': !!fieldValues[field.fieldName],
            'editing': editingField === field.fieldName
          }" @click="goToField(field)">
            <i :class="getFieldIcon(field.type)"></i>
            <div class="field-info">
              <span class="field-label">{{ field.label }}</span>
              <span v-if="!isPreviewMode" class="field-status">
                <i v-if="fieldValues[field.fieldName]" class="fa fa-check text-success"></i>
                <i v-else-if="field.required" class="fa fa-exclamation text-warning"></i>
                <span v-else class="text-muted">Optional</span>
              </span>
            </div>
          </div>
        </div>

        <div v-if="isPreviewMode" class="preview-hint">
          <i class="fa fa-info-circle"></i>
          <span>Click fields to simulate filling them out. Changes won't be saved.</span>
        </div>
      </div>
    </div>

    <!-- Signature Modal -->
    <div v-if="showSignatureModal" class="modal-overlay" @click="closeSignatureModal">
      <div class="signature-modal" @click.stop>
        <div class="modal-header">
          <h4>Add Signature</h4>
          <button class="btn btn-sm btn-default" @click="closeSignatureModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <vue-signature-pad ref="signaturePad" :options="signaturePadOptions" class="signature-pad" />
        </div>
        <div class="modal-footer">
          <button class="btn btn-default" @click="clearSignature">
            Clear
          </button>
          <button class="btn btn-primary" @click="saveSignature">
            Save Signature
          </button>
        </div>
      </div>
    </div>

    <!-- Contextual Field Toolbar -->
    <contextual-field-toolbar
      v-if="showEditorSidebar && selectedAnnotation"
      ref="contextualToolbar"
      :annotation="selectedAnnotation"
      :position="contextualToolbarPosition"
      :scale="pdfScale"
      :all-annotations="annotations"
      :visible="!!selectedAnnotation"
      @update="handleUpdateAnnotation"
      @delete="handleDeleteAnnotation"
      @duplicate="handleDuplicateAnnotation"
      @validate-field-name="validateFieldName"
      @open-modal="handleOpenPropertyModal"
      @close="clearSelection"
    />

    <!-- Field Property Modal -->
    <field-property-modal
      :visible="showPropertyModal"
      :annotation="selectedAnnotation"
      :all-annotations="annotations"
      :initial-tab="propertyModalActiveTab"
      :mode="mode"
      :template-id="templateId"
      @update="handleUpdateAnnotation"
      @delete="handleDeleteAnnotation"
      @validate-field-name="validateFieldName"
      @close="showPropertyModal = false"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import { PDFService } from '../../../common/services/PDFService';
import { getCanvasRelativePosition } from './utils/coordinateUtils';

import PDFViewer from '../../../common/components/PDFViewer.vue';
import AnnotationToolbar from './components/AnnotationToolbar.vue';
import PropertyPanel from './components/PropertyPanel.vue';
import ContextualFieldToolbar from './components/ContextualFieldToolbar.vue';
import FieldPropertyModal from './components/FieldPropertyModal.vue';
// SignatureRequirementsEditor removed - signatures are now just form fields with type='signature'
import FieldGhostPreview from './components/FieldGhostPreview.vue';
import SignatureField from './fields/SignatureField.vue';
import TextField from './fields/TextField.vue';
import SelectField from './fields/SelectField.vue';
import DateField from './fields/DateField.vue';
// VueSignaturePad is registered globally in document-editor.module.js via Vue.use()

export default {
  name: 'PDFDocumentEditor',
  components: {
    PDFViewer,
    AnnotationToolbar,
    PropertyPanel,
    ContextualFieldToolbar,
    FieldPropertyModal,
    // SignatureRequirementsEditor removed - no longer needed
    FieldGhostPreview,
    SignatureField,
    TextField,
    SelectField,
    DateField
    // VueSignaturePad is registered globally via Vue.use() in document-editor.module.js
  },
  props: {
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit', 'preview'].includes(value)
    },
    templateId: {
      type: Number,
      default: null
    },
    documentId: {
      type: Number,
      default: null
    },
    pdfUrl: {
      type: String,
      default: null
    },
    templateName: {
      type: String,
      default: ''
    },
    templateDescription: {
      type: String,
      default: ''
    },
    templateStatus: {
      type: String,
      default: 'draft',
      validator: (value) => ['draft', 'active', 'archived'].includes(value)
    },
    userRole: {
      type: String,
      default: 'student'
    },
    assignedFields: {
      type: Array,
      default: () => []
    },
    initialSignatureRequirements: {
      type: Object,
      default: () => ({
        document_level: [],
        field_level: {}
      })
    },
    initialAnnotations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      pdfFile: null,
      pdfLoaded: false,
      currentPage: 1,
      totalPages: 0,
      pdfScale: 1.0,
      showSignatureModal: false,
      currentSignatureField: null,
      showPropertyModal: false,
      propertyModalActiveTab: 'basic',
      saving: false,
      submitting: false,
      signaturePadOptions: {
        'dotSize': 2,
        'minWidth': 2,
        'maxWidth': 4,
        'backgroundColor': 'rgba(255,255,255,1)',
        'penColor': 'rgba(0,0,0,1)'
      },
      // signature_requirements removed - signatures are now just form fields
      isDraggingField: false,
      isDraggingOver: false,
      dragEnterCount: 0,
      ghostCursorPosition: { x: 0, y: 0 },
      dragFieldType: null,
      // Template metadata (internal state)
      localTemplateName: '',
      localTemplateDescription: '',
      localTemplateStatus: 'draft'
    };
  },
  computed: {
    ...mapGetters('documentEditor', {
      annotations: 'allAnnotations',
      selectedAnnotation: 'selectedAnnotation',
      addingField: 'isAddingField',
      addingFieldType: 'addingFieldType',
      editingField: 'editingField'
    }),

    ...mapState('documentEditor', {
      vuexFieldValues: 'fieldValues'
    }),

    isPreviewMode() {
      return this.mode === 'preview';
    },

    editorHeader() {
      if (this.isPreviewMode) {
        return 'Preview Document Template';
      }

      if (this.isTemplateEditMode) {
        return 'Edit Document Template';
      }

      if ((this.mode || 'create') === 'create') {
        return 'Create Document';
      }

      return 'Edit Document';
    },

    currentPageAnnotations() {
      const annotations = this.$store.getters['documentEditor/getAnnotationsForPage'](this.currentPage);
      console.log('currentPageAnnotations computed - page:', this.currentPage, 'annotations:', annotations);
      return annotations;
    },

    fieldValues() {
      return this.vuexFieldValues;
    },

    userAssignedFields() {
      if (this.mode !== 'edit' && !this.isPreviewMode) return [];

      if (this.isPreviewMode) {
        // Show all fields to simulate complete form experience
        return this.annotations;
      }

      return this.annotations.filter(ann =>
        (this.assignedFields || []).includes(ann.fieldName) ||
        ann.assignableTo.includes(this.userRole || 'student')
      );
    },

    completedFields() {
      return this.userAssignedFields.filter(field =>
        !!this.vuexFieldValues[field.fieldName]
      ).length;
    },

    completionPercentage() {
      if (this.userAssignedFields.length === 0) return 0;
      return Math.round((this.completedFields / this.userAssignedFields.length) * 100);
    },

    allRequiredFieldsCompleted() {
      const requiredFields = this.userAssignedFields.filter(field => field.required);
      return requiredFields.every(field => !!this.vuexFieldValues[field.fieldName]);
    },

    // Determine if we're in template edit mode (admin editing a template)
    // vs document completion mode (user filling out a document)
    isTemplateEditMode() {
      // If we have a templateId prop, we're editing a template
      // This distinguishes from document completion mode where we'd have a documentId
      return this.templateId !== null && this.templateId !== undefined;
    },

    // Check if we should show the editor sidebar (for template creation/editing)
    // Preview mode should NOT show the editor sidebar
    showEditorSidebar() {
      return (this.mode === 'create' || this.isTemplateEditMode) && !this.isPreviewMode;
    },

    // Calculate position for contextual toolbar
    contextualToolbarPosition() {
      if (!this.selectedAnnotation) {
        console.log('[PDFDocumentEditor] contextualToolbarPosition: No selected annotation');
        return null;
      }

      const annotationId = this.selectedAnnotation.id;
      console.log('[PDFDocumentEditor] Looking for field element with ID:', annotationId);

      // Get the actual DOM element of the selected field
      const fieldElement = document.querySelector(`[data-annotation-id="${annotationId}"]`);
      if (!fieldElement) {
        console.warn('[PDFDocumentEditor] Field element NOT FOUND in DOM for annotation:', {
          id: annotationId,
          fieldName: this.selectedAnnotation.fieldName,
          allFieldsInDOM: Array.from(document.querySelectorAll('[data-annotation-id]')).map(el => el.getAttribute('data-annotation-id'))
        });
        return null;
      }

      console.log('[PDFDocumentEditor] Field element FOUND:', fieldElement);

      // Get the field's actual position in viewport coordinates
      const fieldRect = fieldElement.getBoundingClientRect();

      const position = {
        x: fieldRect.left,
        y: fieldRect.top,
        width: fieldRect.width,
        height: fieldRect.height
      };

      console.log('[PDFDocumentEditor] contextualToolbarPosition calculated:', {
        annotation: this.selectedAnnotation.fieldName,
        fieldRect: { left: fieldRect.left, top: fieldRect.top, width: fieldRect.width, height: fieldRect.height },
        finalPosition: position
      });

      return position;
    }
  },
  watch: {
    // Watch for annotation position changes to update toolbar position
    'selectedAnnotation.position': {
      handler(newPosition, oldPosition) {
        if (newPosition && this.selectedAnnotation) {
          console.log('[PDFDocumentEditor] Annotation position changed, triggering toolbar position recalculation');
          // Force the contextualToolbarPosition computed property to recalculate
          this.$forceUpdate();
        }
      },
      deep: true
    },
    // Watch for when a new annotation is selected
    selectedAnnotation: {
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          console.log('[PDFDocumentEditor] Selected annotation changed:', newVal.fieldName);
          // Force position recalculation after DOM updates
          this.$nextTick(() => {
            // Trigger toolbar position update
            this.$forceUpdate();

            // Also trigger toolbar's updatePosition if available
            if (this.$refs.contextualToolbar) {
              this.$refs.contextualToolbar.updatePosition();
            }
          });
        }
      },
      immediate: false
    },
    // initialSignatureRequirements watcher removed - signatures are now just form fields
    templateName: {
      handler(newValue) {
        this.localTemplateName = newValue || '';
      },
      immediate: true
    },
    templateDescription: {
      handler(newValue) {
        this.localTemplateDescription = newValue || '';
      },
      immediate: true
    },
    templateStatus: {
      handler(newValue) {
        this.localTemplateStatus = newValue || 'draft';
      },
      immediate: true
    },
    pdfUrl: {
      handler(newUrl) {
        if (newUrl) {
          console.log('PDF URL provided:', newUrl);
          // Don't set pdfLoaded here - wait for the actual PDF to load via onPdfLoaded event
          // this.pdfLoaded = true;  // REMOVED - this was causing the issue
        }
      },
      immediate: true
    },
    initialAnnotations: {
      handler(newAnnotations) {
        console.log('initialAnnotations watcher triggered:', newAnnotations);
        if (newAnnotations && newAnnotations.length > 0) {
          // Check if annotations array is empty (not using computed property which might not be reactive)
          const currentAnnotations = this.$store.getters['documentEditor/allAnnotations'];
          console.log('Current annotations in store:', currentAnnotations.length);

          if (currentAnnotations.length === 0) {
            console.log('Importing annotations from watcher:', newAnnotations);
            this.importAnnotations(newAnnotations);

            this.$nextTick(() => {
              console.log('Annotations after import (from watcher):', this.annotations);
              console.log('Annotations count in store:', this.annotationCount);
            });
          } else {
            console.log('Skipping import - already have annotations:', currentAnnotations.length);
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    console.log('PDFDocumentEditor mounted with props:', {
      mode: this.mode,
      templateId: this.templateId,
      templateName: this.templateName,
      pdfUrl: this.pdfUrl,
      initialAnnotations: this.initialAnnotations,
      isTemplateEditMode: this.isTemplateEditMode,
      showEditorSidebar: this.showEditorSidebar
    });

    // Only reset editor if we're in create mode
    // In edit mode, we'll wait for annotations to arrive via watcher
    if (this.mode === 'create') {
      // Reset editor state on mount
      this.resetEditor();
    }

    // Import initial annotations if provided (for edit mode)
    // This might not have data yet if props are updated after mount
    if (this.initialAnnotations && this.initialAnnotations.length > 0) {
      console.log('Importing initial annotations from mounted:', this.initialAnnotations);
      this.importAnnotations(this.initialAnnotations);

      // Verify annotations were imported
      this.$nextTick(() => {
        console.log('Annotations after import (mounted):', this.annotations);
        console.log('Annotations count in store (mounted):', this.annotationCount);
      });
    } else if (this.mode === 'edit') {
      console.log('Edit mode but no initial annotations yet - will wait for watcher');
    }

    if (this.mode === 'edit' && this.templateId) {
      this.loadTemplate();
    }

    // Handle clicks outside to deselect annotation
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
    // Clean up Vuex state
    this.resetEditor();
  },
  methods: {
    ...mapActions('documentEditor', [
      'addAnnotation',
      'updateAnnotation',
      'removeAnnotation',
      'moveAnnotation',
      'selectAnnotation',
      'clearSelection',
      'startAddingField',
      'cancelAddingField',
      'startEditingField',
      'stopEditingField',
      'setFieldValue',
      'importAnnotations',
      'exportAnnotations',
      'resetEditor'
    ]),
    handleFileUpload(event) {
      const target = event.target;
      const file = target.files && target.files[0];

      if (file && file.type === 'application/pdf') {
        this.pdfFile = file;
        this.pdfLoaded = true;
      } else {
        alert('Please select a valid PDF file.');
      }
    },

    async loadTemplate() {
      // This would load template data from API
      // For now, we'll simulate it
      this.$emit('load-template', this.templateId);
    },

    onPdfLoaded(data) {
      this.totalPages = data.totalPages;
      this.currentPage = 1;
      this.pdfLoaded = true;  // Set pdfLoaded when PDF actually loads
      this.$emit('pdf-loaded', data);

      // Log for debugging
      console.log('PDF loaded successfully:', {
        totalPages: this.totalPages,
        currentPage: this.currentPage,
        mode: this.mode,
        isTemplateEditMode: this.isTemplateEditMode,
        annotationsCount: this.annotations.length
      });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.clearSelection();
    },

    onScaleChange(scale) {
      this.pdfScale = scale;
    },

    onPdfError(error) {
      console.error('PDF Error:', error);
      this.$emit('error', error);
    },

    getFieldComponent(type) {
      // Return actual component objects instead of strings to avoid Vue.component lookup issues
      const components = {
        signature: SignatureField,
        text: TextField,
        select: SelectField,
        date: DateField
      };
      return components[type] || TextField;
    },

    getFieldIcon(type) {
      const icons = {
        signature: 'fa fa-signature',
        text: 'fa fa-font',
        select: 'fa fa-list',
        date: 'fa fa-calendar'
      };
      return icons[type] || 'fa fa-question';
    },

    getFieldValue(fieldName) {
      return this.$store.getters['documentEditor/getFieldValue'](fieldName);
    },

    updateFieldValue(fieldName, value) {
      this.setFieldValue({ fieldName, value });

      if (this.mode === 'edit') {
        this.autoSaveFieldValue(fieldName, value);
      }
    },

    autoSaveFieldValue(fieldName, value) {
      // Debounced auto-save implementation
      this.$emit('field-changed', { fieldName, value });
    },

    handleStartAddingField(type) {
      console.log('Starting to add field of type:', type);
      this.startAddingField(type);
      console.log('addingField state:', this.addingField, 'addingFieldType:', this.addingFieldType);
    },

    handleCancelAddingField() {
      this.cancelAddingField();
      this.$refs.toolbar.completeAdd();
    },

    handleToolbarDragStart(data) {
      this.isDraggingField = true;
      this.isDraggingOver = false;
      this.dragEnterCount = 0;
      this.dragFieldType = data.type;

      // Add global dragover listener for ghost preview
      document.addEventListener('dragover', this.updateGhostPosition);
    },

    handleToolbarDragEnd() {
      this.isDraggingField = false;
      this.isDraggingOver = false;
      this.dragEnterCount = 0;
      this.dragFieldType = null;

      // Remove global dragover listener
      document.removeEventListener('dragover', this.updateGhostPosition);
    },

    updateGhostPosition(event) {
      this.ghostCursorPosition = {
        x: event.clientX,
        y: event.clientY
      };
    },

    handleDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';

      // Update drag-over state
      if (!this.isDraggingOver) {
        this.isDraggingOver = true;
      }
    },

    handleDragEnter(event) {
      event.preventDefault();
      this.dragEnterCount++;

      if (this.dragEnterCount === 1) {
        this.isDraggingOver = true;
      }
    },

    handleDragLeave(event) {
      this.dragEnterCount--;

      if (this.dragEnterCount === 0) {
        this.isDraggingOver = false;
      }
    },

    handleDrop(event) {
      event.preventDefault();

      // Reset drag state
      this.isDraggingField = false;
      this.isDraggingOver = false;
      this.dragEnterCount = 0;

      // Get field type from drag data
      const fieldType = event.dataTransfer.getData('application/x-field-type') ||
        event.dataTransfer.getData('text/plain');

      if (!fieldType) {
        console.warn('No field type found in drag data');
        return;
      }

      // Get the PDF canvas element for coordinate calculation
      const pdfCanvas = this.$refs.pdfViewer.$refs.pdfCanvas;
      if (!pdfCanvas) {
        console.error('PDF canvas not found');
        return;
      }

      // Calculate PDF coordinates from drop position
      const position = getCanvasRelativePosition(event, pdfCanvas, this.pdfScale);

      if (!position) {
        console.error('Could not calculate drop position');
        return;
      }

      // Create the annotation at the drop position
      console.log('Dropped field type:', fieldType, 'at position:', position);
      this.createAnnotation(fieldType, position.x, position.y);
    },

    handleCanvasClick(event) {
      console.log('Canvas clicked, addingField:', this.addingField, 'addingFieldType:', this.addingFieldType);
      if (!this.addingField || !this.addingFieldType) return;

      // Get the PDF canvas element to calculate relative position
      const pdfCanvas = this.$refs.pdfViewer.$refs.pdfCanvas;
      if (!pdfCanvas) {
        console.error('PDF canvas not found');
        return;
      }

      const canvasRect = pdfCanvas.getBoundingClientRect();
      const x = (event.clientX - canvasRect.left) / this.pdfScale;
      const y = (event.clientY - canvasRect.top) / this.pdfScale;

      console.log('Creating annotation at:', x, y, 'type:', this.addingFieldType);
      console.log('Canvas rect:', canvasRect);
      console.log('Click position:', event.clientX, event.clientY);
      this.createAnnotation(this.addingFieldType, x, y);
      this.handleCancelAddingField();
    },

    createAnnotation(type, x, y) {
      const fieldName = `${type}_${Date.now()}`;

      // Define default sizes for field types
      const fieldSizes = {
        signature: { width: 200, height: 60 },
        text: { width: 150, height: 30 },
        select: { width: 150, height: 40 },
        date: { width: 150, height: 40 }
      };

      const size = fieldSizes[type] || { width: 150, height: 40 };

      // Position centered on click
      const position = {
        x: Math.max(0, x - size.width / 2),
        y: Math.max(0, y - size.height / 2),
        width: size.width,
        height: size.height
      };

      const baseAnnotation = {
        type,
        page: this.currentPage,
        position,
        fieldName,
        label: `${type.charAt(0).toUpperCase() + type.slice(1)} Field`,
        required: false,
        assignableTo: ['student']
      };

      let annotation;

      switch (type) {
        case 'signature':
          annotation = {
            ...baseAnnotation,
            type: 'signature',
            signee_type: 'student',
            properties: {
              level: 'document',
              boxStyle: 'border'
            }
          };
          break;
        case 'text':
          annotation = {
            ...baseAnnotation,
            type: 'text',
            properties: { multiline: false, maxLength: 255, placeholder: 'Enter text...' }
          };
          break;
        case 'select':
          annotation = {
            ...baseAnnotation,
            type: 'select',
            properties: {
              options: [
                { value: 'option1', label: 'Option 1' },
                { value: 'option2', label: 'Option 2' }
              ]
            }
          };
          break;
        case 'date':
          annotation = {
            ...baseAnnotation,
            type: 'date',
            properties: { format: 'MM/DD/YYYY', minDate: '', maxDate: '' }
          };
          break;
        default:
          return;
      }

      console.log('Before addAnnotation - annotations:', this.annotations);
      console.log('Adding annotation:', annotation);

      this.addAnnotation(annotation).then(created => {
        console.log('Created annotation:', created);
        console.log('After addAnnotation - total annotations:', this.annotations.length);
        console.log('Current page annotations:', this.currentPageAnnotations.length);
        console.log('Vuex state annotations:', this.$store.state.documentEditor.annotations);

        // Add a delay to ensure DOM updates
        this.$nextTick(() => {
          console.log('After nextTick - selecting annotation:', created);
          this.selectAnnotation(created);
          console.log('Selected annotation:', this.selectedAnnotation);
        });
      }).catch(error => {
        console.error('Error adding annotation:', error);
      });
    },

    handleSelectAnnotation(annotation) {
      console.log('handleSelectAnnotation called with:', annotation);
      this.selectAnnotation(annotation);
      console.log('After selectAnnotation - selectedAnnotation:', this.selectedAnnotation);

      // Start editing in both edit mode and preview mode to allow field interaction
      if ((this.mode === 'edit' || this.isPreviewMode) && this.userAssignedFields.some(f => f.id === annotation.id)) {
        this.handleStartEditingField(annotation);
      }
    },

    handleStartEditingField(annotation) {
      this.startEditingField(annotation.fieldName);
    },

    handleStopEditingField() {
      this.stopEditingField();
    },

    handleUpdateAnnotation(updatedAnnotation) {
      this.updateAnnotation({ id: updatedAnnotation.id, updates: updatedAnnotation });
    },

    handleUpdateAnnotationPosition(data) {
      this.moveAnnotation(data);
    },

    handleDeleteAnnotation(id) {
      this.removeAnnotation(id);
    },

    handleDuplicateAnnotation(id) {
      const annotation = this.annotations.find(a => a.id === id);
      if (!annotation) return;

      // Create a copy of the annotation with a new ID and slightly offset position
      const duplicated = {
        ...annotation,
        id: undefined, // Will be generated by store
        fieldName: `${annotation.fieldName}_copy`,
        position: {
          ...annotation.position,
          x: annotation.position.x + 20,
          y: annotation.position.y + 20
        },
        properties: { ...annotation.properties }
      };

      this.addAnnotation(duplicated).then(created => {
        this.selectAnnotation(created);
      });
    },

    handleOpenPropertyModal({ annotation, activeTab }) {
      this.propertyModalActiveTab = activeTab;
      this.showPropertyModal = true;
    },

    validateFieldName(annotation) {
      const isValid = this.$store.getters['documentEditor/validateFieldName'](annotation.fieldName, annotation.id);
      if (!isValid) {
        annotation.fieldName = `${annotation.fieldName}_${Date.now()}`;
        this.handleUpdateAnnotation(annotation);
      }
    },

    goToField(field) {
      // Navigate to the page containing this field
      if (field.page !== this.currentPage) {
        this.currentPage = field.page;
        this.$refs.pdfViewer.goToPage(field.page);
      }

      this.selectAnnotation(field);
      this.startEditingField(field.fieldName);
    },

    openSignatureModal(annotation) {
      this.currentSignatureField = annotation;
      this.showSignatureModal = true;
    },

    closeSignatureModal() {
      this.showSignatureModal = false;
      this.currentSignatureField = null;
    },

    clearSignature() {
      this.$refs.signaturePad.clearSignature();
    },

    saveSignature() {
      if (!this.currentSignatureField) return;

      const signatureData = this.$refs.signaturePad.saveSignature();
      if (!signatureData.isEmpty) {
        this.updateFieldValue(this.currentSignatureField.fieldName, signatureData.data);
      }

      this.closeSignatureModal();
    },

    handleDocumentClick(event) {
      // Deselect annotation if clicking outside
      if (!this.addingField && this.selectedAnnotation) {
        const target = event.target;
        // Don't deselect if clicking on:
        // - annotation field
        // - property panel
        // - contextual toolbar
        // - modal overlay or modal content
        if (!target.closest('.annotation-field') &&
            !target.closest('.property-panel') &&
            !target.closest('.contextual-field-toolbar') &&
            !target.closest('.modal-overlay') &&
            !target.closest('.field-property-modal')) {
          this.clearSelection();
        }
      }
    },

    async saveTemplate() {
      if (this.saving) return;

      this.saving = true;

      try {
        const templateData = {
          name: this.localTemplateName || 'Untitled Template',
          description: this.localTemplateDescription || '',
          status: this.localTemplateStatus || 'draft',
          annotations: await this.exportAnnotations(),
          // signature_requirements removed - signatures are now part of annotations
          pdfFile: this.pdfFile
        };

        this.$emit('save-template', templateData);
      } catch (error) {
        console.error('Error saving template:', error);
        this.$emit('error', error);
      } finally {
        this.saving = false;
      }
    },

    async submitDocument() {
      if (this.submitting) return;

      this.submitting = true;

      try {
        const documentData = {
          documentId: this.documentId,
          fieldValues: this.fieldValues,
          completed: this.allRequiredFieldsCompleted
        };

        this.$emit('submit-document', documentData);
      } catch (error) {
        console.error('Error submitting document:', error);
        this.$emit('error', error);
      } finally {
        this.submitting = false;
      }
    }
  }
}
</script>

<style scoped>
/* DriveScout Document Editor - Clean Admin Theme */
.pdf-document-editor {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f7fafc;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: transparent;
  border-bottom: none;
}

.header-left h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
}

.template-name {
  font-size: 14px;
  color: #718096;
  margin-left: 10px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .btn-success {
  background-color: #64BC5F;
  border-color: #64BC5F;
}

.header-actions .btn-success:hover {
  background-color: #3D9633;
  border-color: #3D9633;
}

.header-actions .btn-primary {
  background-color: #4a90d9;
  border-color: #4a90d9;
  color: #fff;
}

.header-actions .btn-primary:hover {
  background-color: #357abd;
  border-color: #357abd;
}

.header-actions .btn-default {
  background-color: #fff;
  border-color: #cbd5e0;
  color: #4a5568;
}

.header-actions .btn-default:hover {
  background-color: #f7fafc;
  border-color: #a0aec0;
}

.editor-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;  /* Allow flex child to shrink below content size */
}

.editor-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: #e8edf2;
  min-height: 0;  /* Allow flex child to shrink below content size */
}

.editor-sidebar {
  width: 320px;
  min-width: 280px;
  max-width: 320px;
  background-color: #fff;
  border-left: 1px solid #e2e8f0;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  /* Ensure sidebar takes full height */
}

.template-metadata-section {
  padding: 16px;
  background-color: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
  /* Don't shrink, maintain minimum size */
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #2d3748;
  background-color: #fff;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  transition: border-color 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.form-control::placeholder {
  color: #a0aec0;
}

.pdf-upload-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border: 2px dashed #cbd5e0;
  margin: 32px;
  border-radius: 8px;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.pdf-upload-area:hover {
  border-color: #64BC5F;
  background-color: #f0fff4;
}

.upload-content {
  text-align: center;
  color: #718096;
}

.upload-content i {
  color: #64BC5F;
  margin-bottom: 16px;
}

.upload-content h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #2d3748;
}

.upload-content p {
  margin: 0 0 20px 0;
}

.upload-content .btn-primary {
  background-color: #64BC5F;
  border-color: #64BC5F;
  color: #fff;
  font-weight: 500;
}

.upload-content .btn-primary:hover {
  background-color: #3D9633;
  border-color: #3D9633;
}

.file-input {
  display: none;
}

.drop-zone {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  pointer-events: none;
  border-radius: 4px;
}

.drop-zone.drag-active {
  background-color: rgba(100, 188, 95, 0.03);
  border: 2px dashed rgba(100, 188, 95, 0.3);
  pointer-events: auto;
}

.drop-zone.drag-over {
  background-color: rgba(100, 188, 95, 0.08);
  border: 2px solid rgba(100, 188, 95, 0.6);
  cursor: copy;
  pointer-events: auto;
}

.click-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: crosshair;
  z-index: 1000;
  background-color: rgba(100, 188, 95, 0.02);
}

.annotations-summary {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
  /* Don't shrink, maintain minimum size */
}

.annotations-summary h5 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.annotations-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.annotation-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  min-width: 0;
  /* Allow flex items to shrink below content width */
}

.annotation-item:hover {
  background-color: #f7fafc;
}

.annotation-item.selected {
  background-color: #ebf8ff;
  border: 1px solid #4299e1;
}

.annotation-item i {
  font-size: 14px;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.annotation-item span:first-of-type {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  /* Allow text to shrink and ellipsis to work */
}

.annotation-type {
  font-size: 11px;
  color: #718096;
  text-transform: uppercase;
  font-weight: 600;
  flex-shrink: 0;
}

.completion-progress {
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f7fafc;
  border-radius: 8px;
}

.completion-progress h5 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background-color: #48bb78;
  transition: width 0.3s ease;
}

.field-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.field-item:hover {
  border-color: #cbd5e0;
}

.field-item.completed {
  background-color: #f0fff4;
  border-color: #9ae6b4;
}

.field-item.editing {
  border-color: #4299e1;
  background-color: #ebf8ff;
}

.field-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #2d3748;
}

.field-status {
  font-size: 12px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.signature-modal {
  background-color: #fff;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 80%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-body {
  padding: 20px;
}

.signature-pad {
  width: 100%;
  height: 300px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e2e8f0;
}

/* Mobile responsive adjustments for drag-drop */
@media (max-width: 768px) {
  .click-overlay {
    /* Larger touch targets on mobile */
    min-height: 44px;
  }

  .click-overlay.drag-over {
    border-width: 4px;
    background-color: rgba(66, 153, 225, 0.12);
  }

  .editor-sidebar {
    width: 280px;
    min-width: 240px;
    max-width: 280px;
    padding: 15px;
  }
}

/* Tablet adjustments */
@media (min-width: 769px) and (max-width: 1024px) {
  .click-overlay.drag-over {
    background-color: rgba(66, 153, 225, 0.1);
  }
}

/* Accessibility: High contrast mode */
@media (prefers-contrast: high) {
  .click-overlay.drag-active {
    border-width: 3px;
    border-color: rgba(66, 153, 225, 0.7);
  }

  .click-overlay.drag-over {
    border-width: 4px;
    border-color: rgb(66, 153, 225);
    background-color: rgba(66, 153, 225, 0.15);
  }

  .click-overlay.drag-invalid {
    border-width: 3px;
    border-color: rgb(239, 68, 68);
  }
}

/* Accessibility: Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .click-overlay {
    transition: none;
  }

  .click-overlay.drag-over {
    animation: none;
  }

  .click-overlay.drop-success {
    animation: none;
  }

  @keyframes pulse {

    /* Disable pulsing animation */
    0%,
    100% {
      border-color: rgba(66, 153, 225, 0.5);
    }
  }
}

/* Preview Mode Styles */
.editor-header.preview-mode-header {
  background: transparent;
  border-bottom-color: #e2e8f0;
}

.preview-mode-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #718096;
  border-radius: 6px;
  color: #fff;
  margin-right: 8px;
}

.preview-mode-badge i {
  font-size: 16px;
}

.preview-mode-badge span {
  font-size: 14px;
  font-weight: 600;
}

.preview-mode-badge small {
  font-size: 11px;
  opacity: 0.85;
  padding-left: 8px;
  border-left: 1px solid rgba(255,255,255,0.3);
}

/* Preview Sidebar */
.preview-sidebar {
  background-color: #fff;
}

.preview-info {
  text-align: center;
  padding: 20px 16px;
  background: linear-gradient(135deg, #ebf8ff 0%, #e6fffa 100%);
  border-radius: 8px;
  margin-bottom: 20px;
}

.preview-info-icon {
  width: 48px;
  height: 48px;
  background-color: #4299e1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.preview-info-icon i {
  font-size: 20px;
  color: #fff;
}

.preview-info h5 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.preview-info p {
  margin: 0;
  font-size: 13px;
  color: #718096;
}

.preview-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background-color: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  margin-top: 20px;
}

.preview-hint i {
  color: #d97706;
  flex-shrink: 0;
  margin-top: 2px;
}

.preview-hint span {
  font-size: 12px;
  color: #92400e;
  line-height: 1.4;
}
</style>
